import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Bot, Sparkles, Bell, MessageSquare, ArrowRight } from "lucide-react";

const features = [
  {
    icon: Sparkles,
    title: "Clasifica automáticamente",
    description: "Sube tus facturas y CONTA las organiza por tipo de gasto en segundos.",
  },
  {
    icon: Bot,
    title: "Detecta deducciones",
    description: "Encuentra gastos deducibles que no sabías que tenías. Más ahorro, menos impuestos.",
  },
  {
    icon: Bell,
    title: "Alertas predictivas",
    description: "Te avisa antes de que venza cualquier plazo fiscal. Nunca más recargos.",
  },
  {
    icon: MessageSquare,
    title: "Chat 24/7",
    description: "Pregunta cualquier duda fiscal y obtén respuestas claras al instante.",
  },
];

const chatMessages = [
  { role: "user", text: "¿Puedo deducir la gasolina de mi auto?" },
  { role: "ai", text: "¡Claro! Si usas tu vehículo para actividades de tu negocio, puedes deducir hasta el 75% de los gastos de gasolina. Solo necesitas guardar tus facturas y tickets. 🚗" },
  { role: "user", text: "¿Y el seguro del carro también?" },
  { role: "ai", text: "Sí, el seguro de auto también es deducible en la misma proporción. Detecté que tienes 3 pagos de seguro este año sin clasificar. ¿Quieres que los agregue?" },
];

export function AISection() {
  return (
    <section className="section-padding bg-gradient-to-b from-primary to-primary/95 text-primary-foreground overflow-hidden">
      <div className="container-custom">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/20 text-secondary mb-6">
              <Bot className="w-4 h-4" />
              <span className="text-sm font-medium">Conoce a CONTA</span>
            </div>

            <h2 className="text-3xl md:text-4xl font-heading font-bold mb-6">
              El futuro de la contabilidad{" "}
              <span className="text-secondary">ya llegó</span>
            </h2>

            <p className="text-lg text-primary-foreground/70 mb-8">
              CONTA es tu asistente contable inteligente, impulsado por IA de última generación. 
              Trabaja 24/7 para que tú no tengas que hacerlo.
            </p>

            <div className="grid sm:grid-cols-2 gap-4 mb-8">
              {features.map((feature, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-primary-foreground/5 border border-primary-foreground/10"
                >
                  <div className="p-2 rounded-lg bg-secondary/20">
                    <feature.icon className="w-5 h-5 text-secondary" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 text-base">{feature.title}</h3>
                    <p className="text-sm text-primary-foreground/60">{feature.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            <Button size="lg" className="btn-primary group" asChild>
              <Link to="/asistente-ia">
                Prueba el asistente gratis
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>
          </motion.div>

          {/* Chat Mockup */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="absolute -top-10 -right-10 w-40 h-40 bg-secondary/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-accent/20 rounded-full blur-3xl" />
            
            <div className="relative bg-card rounded-3xl shadow-2xl overflow-hidden">
              {/* Chat Header */}
              <div className="bg-muted/50 px-6 py-4 border-b border-border/50 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                  <Bot className="w-5 h-5 text-secondary-foreground" />
                </div>
                <div>
                  <p className="font-semibold text-card-foreground">CONTA</p>
                  <p className="text-xs text-muted-foreground">Tu asistente IA • En línea</p>
                </div>
              </div>

              {/* Chat Messages */}
              <div className="p-6 space-y-4 min-h-[300px]">
                {chatMessages.map((msg, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.4 + i * 0.2 }}
                    className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm ${
                        msg.role === "user"
                          ? "bg-secondary text-secondary-foreground rounded-br-md"
                          : "bg-muted text-card-foreground rounded-bl-md"
                      }`}
                    >
                      {msg.text}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Chat Input */}
              <div className="px-6 pb-6">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border/50">
                  <input
                    type="text"
                    placeholder="Escribe tu duda fiscal..."
                    className="flex-1 bg-transparent text-sm placeholder:text-muted-foreground outline-none text-card-foreground"
                    disabled
                  />
                  <Button size="sm" className="bg-secondary text-secondary-foreground">
                    Enviar
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
